import { faCircleXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Label, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import Button from "../../../../../../components/Button";
import Select from "../../../../../../components/Inputs/Select";
import Modal from "../../../../../../components/Modal";
import useApi from "../../../../../../utils/api";
import { useActionWithAccess } from "../../../../../../utils/hooks/access";
import { useVisible } from "../../../../../../utils/hooks/useVisible";
import { formatTimeSheetParams } from "../../../../api/helpers";
import { useDeleteTimeSheet } from "../../../../api/useDeleteTimeSheet";
import { useDateFilters } from "../../../../hooks/useDateFilters";

export const overridableRules = [
    "automaticBreaks", 
    "guaranteeRule--shift",
    "guaranteeRule--daily",
    "guaranteeRule--weekly",
    "guaranteeRule--weekend",
    "nonWorkedHoliday",
    "workedHoliday",
];

export const useRemoveActions = ({ refetch, setState, filters, userId, isMyTimesheet }) => {
    const { t } = useTranslation();
    const [rules, setRules] = useState([]);
    const { from, to } = useDateFilters();
    const { authGet } = useApi();
    const { open, visible, selected, close: _close } = useVisible();
    const close = useCallback(() => {
        _close();
        setRules([]);
    }, [_close]);

    const {
        remove: baseRemove,
        removeMany: baseRemoveMany,
    } = useDeleteTimeSheet();

    const removeOne = useActionWithAccess(
        useCallback((id) => {
            baseRemove(id, async (id) => {
                return authGet(`/time-sheet/my-team`, {
                    params: {
                        ...formatTimeSheetParams({
                            from,
                            to,
                            filters
                        }),
                        userId,
                    },
                }).then(async (response) => {
                    await refetch(response);
                    setState((prev) => ({
                        ...prev,
                        data: prev?.data?.filter(
                            (prevTimeSheet) => id !== prevTimeSheet?.id
                        ),
                        totalItems: prev.totalItems - 1,
                    }));
                    close();
                })
            })
        }, [baseRemove, authGet, from, to, filters, userId, refetch, setState, close]),
        isMyTimesheet ? "timeSheet.canDeleteMyTimesheet" : "timeSheet.canDelete",
    );

    const removeMany = useActionWithAccess(
        useCallback((ids, data) => {
            baseRemoveMany(ids, data, userId, async () => {
                return authGet(`/time-sheet/my-team`, {
                    params: {
                        ...formatTimeSheetParams({
                            from,
                            to,
                            filters
                        }),
                        userId,
                    },
                }).then(async (response) => {
                    await refetch(response);
                    setState((prev) => ({
                        ...prev,
                        data: prev?.data?.filter(
                            (prevTimeSheet) => !ids.includes(prevTimeSheet?.id)
                        ),
                        totalItems: prev.totalItems - ids.length,
                    }));
                    close();
                })
            });
        }, [baseRemoveMany, userId, authGet, from, to, filters, refetch, setState, close]),
        isMyTimesheet ? "timeSheet.canDeleteMyTimesheet" : "timeSheet.canDelete",
    );

    const { removeWithoutOverride, removeWithOverride } = useMemo(() => {
        const data = selected?.data || {};
        const dates = selected?.dates || [];
        return {
            removeWithoutOverride: Array.isArray(data) ? () => removeMany(data, { rules: [], dates, override: false }, userId) : () => removeOne(data),
            removeWithOverride: () => removeMany(data, { rules, dates, override: true }, userId)
        }
    }, [removeMany, removeOne, rules, selected?.data, selected?.dates, userId]);

    const modal = useMemo(() => {
        const timesheets = selected?.timesheets?.filter((timesheet) => selected?.data.includes(timesheet.id));
        const appliedRules = overridableRules.filter((rule) => {
            return timesheets?.some((timesheet) => timesheet?.appliedRules?.includes(rule));
        });

        const count = Array.isArray(selected?.data || {}) ? selected?.data.length : 1;

        return (
            <Modal isOpen={visible} centered>
                <ModalHeader className="d-flex align-items-center justify-content-center py-4 w-100">
                    <FontAwesomeIcon
                        icon={faCircleXmark}
                        className="text-danger w-100 mb-3"
                        fontSize={70}
                    />
                    <h2 className="mb-0 text-center font-weight-bolder">
                        {t("are-you-sure")}
                    </h2>
                </ModalHeader>

                <ModalBody className="d-flex flex-column justify-content-center py-0 mb-4">
                    <p className="text-center">
                        {t("do-you-want-to-delete-timesheets", { count })}
                    </p>

                    {appliedRules?.length > 0 && (
                        <div>
                            <Label>
                                {t("select-options-when-override")}:
                            </Label>
                            <Select
                                placeholder={t("override-rule")}
                                mode="multiple"
                                onChange={(value) => setRules(value)}
                            >
                                {appliedRules.map((rule) => (
                                    <Select.Option key={rule} value={rule}>
                                        {t(rule)}
                                    </Select.Option>
                                ))}
                            </Select>
                        </div>
                    )}
                </ModalBody>

                <ModalFooter className="d-flex justify-content-center align-items-center w-full mx-0 p-0">
                    <Button onClick={close}>
                        {t("cancel")}
                    </Button>
                    {appliedRules?.length > 0 && (
                        <Button
                            className="border border-danger shadow-none text-danger bg-white"
                            disabled={appliedRules?.length > 0 ? rules?.length === 0 : false}
                            onClick={removeWithOverride}
                        >
                            {t("continue-with-override")}
                        </Button>
                    )}
                    <Button
                        color="danger"
                        onClick={removeWithoutOverride}
                    >
                        {appliedRules?.length > 0 ? t("continue-without-override") : t("confirm")}
                    </Button>
                </ModalFooter>
            </Modal>
        );
    }, [close, removeWithOverride, removeWithoutOverride, rules?.length, selected?.data, selected?.timesheets, t, visible]);

    const remove = useActionWithAccess(open, isMyTimesheet ? "timeSheet.canDeleteMyTimesheet" : "timeSheet.canDelete");

    return { modal, remove };
}
